import log from "loglevel";
import * as Sentry from "@sentry/browser";
import UserState from "./stores/UserState";
import { getDeviceId } from "./utils/deviceId";

// Chrome now allows bfcache for Cache-Control: no-store pages, so DrawingWeb.js/.wasm
// (loaded once per page lifetime, see DrawingSingleton) can be silently restored from a
// frozen tab instead of being re-fetched. Tracking this lets us tell that apart from a
// genuine Protection Server response when investigating DrawingWebDisabledError.
let bfcacheRestored = false;
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    bfcacheRestored = true;
    Sentry.setTag("bfcacheRestored", "true");
    log.debug("Page restored from bfcache");
  }
});

type ExceptionListener = (
  error: Error,
  context?: Record<string, unknown>,
) => void;
let exceptionListener: ExceptionListener | null = null;

export class Logger {
  static wasBfcacheRestored(): boolean {
    return bfcacheRestored;
  }
  static init() {
    if (import.meta.env.DR_SENTRY_DSN != "") {
      Sentry.init({
        dsn: import.meta.env.DR_SENTRY_DSN,
        environment: import.meta.env.DR_ENV,
        release: __APP_VERSION__,
        sampleRate: 1,
        replaysOnErrorSampleRate: 1,
        replaysSessionSampleRate: 0,
        beforeSend(event, hint) {
          const error = hint?.originalException;
          if (
            error instanceof TypeError &&
            error.message === "Failed to fetch" &&
            !navigator.onLine
          ) {
            return null;
          }
          return event;
        },
        ignoreErrors: [
          /Failed to (update|register|unregister) a ServiceWorker/i,
          /bad HTTP response code .* was received when fetching the script/i,
        ],
        integrations: [
          Sentry.replayIntegration({
            // Additional SDK configuration goes in here, for example:
            maskAllText: false,
            blockAllMedia: false,
            stickySession: true,
            networkDetailAllowUrls: ["/DrawingJS/"],
            networkRequestHeaders: ["X-Custom-Header"],
            networkResponseHeaders: ["X-Custom-Header"],
            networkCaptureBodies: false,
          }),
          //Sentry.replayCanvasIntegration(),
        ],
      });
      Sentry.setTag("deviceId", getDeviceId());
    }

    // set log level
    log.setLevel(import.meta.env.DR_LOG_LEVEL);
  }
  static trace(message: string) {
    log.trace(message);
  }
  static debug(...args: any) {
    log.debug(...args);
  }
  static info(message: string) {
    log.info(message);
  }
  static warn(message: string) {
    log.warn(message);
  }
  static error(message: string) {
    log.error(message);
  }
  static async vsfError(message: string, drawingId: number, vsf: ArrayBuffer) {
    this.error(message + ` (drawingId: ${drawingId})`);
    Sentry.getCurrentScope().addAttachment({
      filename: `drawing-${drawingId}.vsfx`,
      data: new Uint8Array(vsf),
      contentType: "application/octet-stream",
    });
    Sentry.captureException(new Error(message));
    Sentry.getCurrentScope().clearAttachments();
  }
  static breadcrumb(message: string, data?: Record<string, unknown>): void {
    Sentry.addBreadcrumb({ category: "oda", message, data, level: "debug" });
  }
  static setContext(name: string, data: Record<string, unknown>): void {
    Sentry.setContext(name, data);
  }
  /** Reçoit aussi les exceptions capturées (banc d'essai de la fusion, en dev). */
  static setExceptionListener(listener: ExceptionListener | null): void {
    exceptionListener = listener;
  }
  static captureException(error: unknown, context?: Record<string, unknown>) {
    const err = error instanceof Error ? error : new Error(String(error));
    exceptionListener?.(err, context);
    Sentry.withScope((scope) => {
      if (context) {
        scope.setContext("context", context);
      }
      Sentry.captureException(err);
    });
  }
  static identify() {
    if (!UserState.data) return;
    Sentry.setUser({ email: UserState.data.email });
  }
}
